/**
 * PhotoSwipe lightbox over links carrying data-pswp-width/-height (and an optional
 * data-caption). Loaded on first open so pages that never open one don't pay for it.
 */
export async function createLightbox(links, { onChange } = {}) {
    const { default: PhotoSwipeLightbox } = await import('photoswipe/lightbox');

    const lightbox = new PhotoSwipeLightbox({
        dataSource: [...links],
        pswpModule: () => import('photoswipe'),
        bgOpacity: 1,
        // Keeps the image clear of the counter, arrows and caption rather than under them.
        paddingFn: ({ x }) => (x < 768
            ? { top: 56, bottom: 72, left: 0, right: 0 }
            : { top: 64, bottom: 80, left: 80, right: 80 }),
    });

    lightbox.on('uiRegister', () => {
        lightbox.pswp.ui.registerElement({
            name: 'caption',
            order: 9,
            appendTo: 'root',
            className: 'absolute inset-x-0 bottom-0 px-4 py-6 text-center text-sm text-white text-pretty empty:hidden',
            onInit: (el, pswp) => {
                const update = () => { el.textContent = pswp.currSlide?.data.element?.dataset.caption ?? ''; };
                pswp.on('change', update);
                update();
            },
        });
    });

    if (onChange) {
        lightbox.on('change', () => onChange(lightbox.pswp.currIndex));
    }

    lightbox.init();
    return lightbox;
}

/** Alpine component for a zoomable image: x-data on the link itself, or on a wrapper around several. */
export default () => ({
    /** @type {ReturnType<typeof createLightbox> | null} */
    lightbox: null,

    async open(index = 0) {
        const selector = 'a[data-pswp-width]';
        const links = this.$root.matches(selector) ? [this.$root] : this.$root.querySelectorAll(selector);
        this.lightbox ??= createLightbox(links);
        (await this.lightbox).loadAndOpen(index);
    },

    destroy() {
        this.lightbox?.then((lightbox) => lightbox.destroy());
    },
});
